import { all, and, VBoolean } from 'alkali'
import { createExtractionRequest as createExtractionReq, ExtractionItem, ExtractionMeta } from 'services/extract/generate'
import * as _ from 'lodash'

interface Update {
	meta?: ExtractionMeta
	updates: ExtractionItem[]
	generated?: any
}

interface GeneratedExtraction {
	extractTime: number
	updates: Update[]
}

/**
 * UpdateModel abstraction, captures validation state, whether there are changes, and how to generate updates
 */
export default interface UpdateModel {
	readonly hasChanges: VBoolean
	readonly isValid: VBoolean
	readonly isSelfValid: VBoolean
	generateUpdates(id: number): Update
}

/**
 * We can submit if any models have changes, and all models are valid
 */
export const canSubmit = (...args: UpdateModel[]) =>
	and(
		all(args.map(m => m.isValid), (...valids) => _.every(valids)),
		all(args.map(m => m.hasChanges), (...changes) => _.some(changes))
	)

/**
 * Generates Updates for models that have changes and are valid
 */
export function generateModelUpdates(...models: UpdateModel[]): GeneratedExtraction {
	const extractTime = Date.now()
	return {
		extractTime,
		updates: models.filter(m => m.hasChanges.valueOf() && m.isSelfValid.valueOf()).map(m =>
			m.generateUpdates(extractTime)
		)
	}
}

/**
 * Create an extraction request from a list of UpdateModels
 */
export function composeExtractionRequest(effectiveStudy, user, ...models: UpdateModel[]) {
	return createExtractionRequest(effectiveStudy, user, generateModelUpdates(...models))
}

/**
 * Creates an extract API request body from a generated extraction
 */
export function createExtractionRequest(effectiveStudy, user, generatedExtraction: GeneratedExtraction) {
	const { extractTime, updates } = generatedExtraction
	const modelUpdates = updates.flatMap(u => u.updates)
	// if no valid updates, just return
	if (!_.compact(modelUpdates).length) return
	// hack: find the first update that provides metadata
	const meta = updates.find(u => u?.meta)?.meta
	return createExtractionReq(extractTime, meta, modelUpdates, effectiveStudy, user)
}